import type { TFunction } from "i18next";
import { CatalogImageOwner } from "../../../features/floorplan3d/catalog/catalogQueries";
import {
  FilterOption,
  LibraryFilterChip,
  LibraryFilterFacets,
  LibraryFilterState,
} from "./libraryTypes";

export const PRICE_PRESETS: Array<{
  labelKey: string;
  from: number;
  to: number | "max";
}> = [
  { labelKey: "productLibrary.pricePreset1k5k", from: 1000, to: 5000 },
  { labelKey: "productLibrary.pricePreset5k10k", from: 5000, to: 10000 },
  { labelKey: "productLibrary.pricePreset10k200k", from: 10000, to: 200000 },
  { labelKey: "productLibrary.pricePreset200kPlus", from: 200000, to: "max" },
];

export const formatSekAmount = (value: number, locale?: string) =>
  new Intl.NumberFormat(locale ?? "sv-SE").format(Math.round(value));

export const formatFilterButtonLabel = (label: string, count: number): string =>
  count > 0 ? `${label} (${count})` : label;

export const formatSekPrice = (
  value?: number | null,
  locale?: string,
): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return `${formatSekAmount(value, locale)} SEK`;
};

export const toFilterOptions = (
  values: Array<{
    id?: string | null;
    name?: string | null;
    parent?: { name?: string | null } | null;
    logo?: string | null;
  } | null | undefined>,
): FilterOption[] => {
  const seen = new Set<string>();
  return values
    .filter((value): value is NonNullable<typeof value> =>
      Boolean(value?.id && value?.name),
    )
    .filter((value) => {
      if (seen.has(value.id as string)) return false;
      seen.add(value.id as string);
      return true;
    })
    .map((value) => ({
      id: value.id as string,
      name: value.name as string,
      parentName: value.parent?.name ?? undefined,
      logo: value.logo ?? undefined,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
};

type CategoryNodeInput = {
  id?: string | null;
  name?: string | null;
  parent?: { name?: string | null } | null;
  children?: Array<CategoryNodeInput | null> | null;
};

/**
 * Preserve the published 3D category tree (root → mid → leaf) from GraphQL.
 */
export const toCategoryTree = (
  values: Array<CategoryNodeInput | null | undefined> | null | undefined,
): FilterOption[] =>
  (values ?? [])
    .filter((value): value is CategoryNodeInput =>
      Boolean(value?.id && value?.name),
    )
    .map((value) => ({
      id: value.id as string,
      name: value.name as string,
      parentName: value.parent?.name ?? undefined,
      children: toCategoryTree(value.children),
    }))
    .sort((left, right) => left.name.localeCompare(right.name));

/**
 * Flatten a category tree for pill matching, chips, and id lookups.
 */
export const flattenCategoryTree = (nodes: FilterOption[]): FilterOption[] => {
  const flattened: FilterOption[] = [];
  const walk = (items: FilterOption[]) => {
    for (const item of items) {
      flattened.push(item);
      if (item.children && item.children.length > 0) {
        walk(item.children);
      }
    }
  };
  walk(nodes);
  return flattened;
};

export const compoundCategoryIds = (filters: LibraryFilterState): string[] => [
  ...filters.categoryIds,
  ...filters.colorIds,
  ...filters.roomIds,
  ...filters.shapeIds,
  ...filters.materialIds,
  ...filters.patternIds,
  ...filters.styleIds,
];

export const productColorNames = (product: CatalogImageOwner): string =>
  (product.categoriesMeta ?? [])
    .filter((category) => category?.parent?.name === "Color")
    .map((category) => category?.name)
    .filter((name): name is string => Boolean(name))
    .join(", ");

export const productImages = (product: CatalogImageOwner): string[] => {
  const regular = [
    ...(product.images ?? []),
    ...(product.additionalImages ?? []),
  ].filter((url): url is string => Boolean(url));
  const ai = (product.aiGeneratedImages ?? [])
    .map((image) => image?.imageUrl)
    .filter((url): url is string => Boolean(url));
  return Array.from(new Set([...regular, ...ai]));
};

const optionLabel = (options: FilterOption[], id: string) =>
  options.find((option) => option.id === id)?.name ?? id;

const chipsFor = (
  key: LibraryFilterChip["key"],
  ids: string[],
  options: FilterOption[],
): LibraryFilterChip[] =>
  ids.map((id) => ({ key, id, label: optionLabel(options, id) }));

export const activeFilterChips = (
  filters: LibraryFilterState,
  facets: LibraryFilterFacets,
  options?: { locale?: string; t?: TFunction },
): LibraryFilterChip[] => {
  const locale = options?.locale ?? "sv-SE";
  const chips: LibraryFilterChip[] = [
    ...chipsFor("category", filters.categoryIds, flattenCategoryTree(facets.categories)),
    ...chipsFor("brand", filters.brandNames, facets.brands),
    ...chipsFor("supplier", filters.supplierIds, facets.suppliers),
    ...chipsFor("color", filters.colorIds, facets.colors),
    ...chipsFor("shape", filters.shapeIds, facets.shapes),
    ...chipsFor("material", filters.materialIds, facets.materials),
    ...chipsFor("style", filters.styleIds, facets.styles),
    ...chipsFor("pattern", filters.patternIds, facets.patterns),
    ...chipsFor("room", filters.roomIds, facets.rooms),
  ];
  if (filters.priceFrom != null || filters.priceTo != null) {
    const from = filters.priceFrom ?? facets.priceMin ?? 0;
    const to = filters.priceTo ?? facets.priceMax;
    const fromLabel = `${formatSekAmount(from, locale)} SEK`;
    const label =
      options?.t && to == null
        ? options.t("productLibrary.priceFrom", { from: fromLabel })
        : options?.t && to != null
          ? options.t("productLibrary.priceFromTo", {
              from: fromLabel,
              to: `${formatSekAmount(to, locale)} SEK`,
            })
          : to == null
            ? `${fromLabel}+`
            : `${formatSekAmount(from, locale)} – ${formatSekAmount(to, locale)}`;
    chips.push({
      key: "price",
      id: "price",
      label,
    });
  }
  return chips;
};

export const moreFilterCount = (facets: LibraryFilterFacets) =>
  [
    facets.shapes,
    facets.materials,
    facets.styles,
    facets.patterns,
    facets.rooms,
  ].filter((options) => options.length > 0).length;
